import React from 'react';
import '@/App.css';
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { SessionProvider, useSession } from '@/state/session';
import { DataProvider } from '@/state/data';
import { DemoStoreProvider } from '@/state/store';
import { CatalogProvider } from '@/state/catalog';
import { CartProvider } from '@/state/cart';
import { getScenario } from '@/fixtures/scenarios';
import { RecordViewerProvider } from '@/components/app/RecordViewer';
import { DemoProfileProvider } from '@/state/profile';
import { AppShell } from '@/components/app/AppShell';
import LoginPage from '@/features/LoginPage';
import OversiktPage from '@/features/OversiktPage';
import ProduksjonPage from '@/features/ProduksjonPage';
import MaskinerPage from '@/features/MaskinerPage';
import MaskinDetaljPage from '@/features/MaskinDetaljPage';
import SammenlignPage from '@/features/SammenlignPage';
import AvvikPage from '@/features/AvvikPage';
import AvvikDetaljPage from '@/features/AvvikDetaljPage';
import AvvikSakPage from '@/features/AvvikSakPage';
import RapporterPage from '@/features/RapporterPage';
import ProduksjonsrapportPage from '@/features/ProduksjonsrapportPage';
import DriftRapportPage from '@/features/DriftRapportPage';
import ProsjekterPage from '@/features/ProsjekterPage';
import ProsjektDetaljPage from '@/features/ProsjektDetaljPage';
import DatakilderPage from '@/features/DatakilderPage';
import AssistentPage from '@/features/AssistentPage';
import TimerPage from '@/features/TimerPage';
import MinDagPage from '@/features/MinDagPage';
import VareforbrukPage from '@/features/VareforbrukPage';
import InnstillingerPage from '@/features/InnstillingerPage';
import SiteLayout from '@/features/site/SiteLayout';
import LandingPage from '@/features/site/LandingPage';
import ProductsPage from '@/features/site/ProductsPage';
import ProductDetailPage from '@/features/site/ProductDetailPage';
import CheckoutPage from '@/features/site/CheckoutPage';
import OrderConfirmationPage, { RequestConfirmationPage } from '@/features/site/OrderConfirmationPage';
import ProjectRequestPage from '@/features/site/ProjectRequestPage';
import { EmptyState } from '@/components/app/States';
import { Button } from '@/components/ui/button';

/** Beskyttede ruter omdirigerer til /login når økten mangler. */
function Protected() {
  const { session } = useSession();
  const location = useLocation();
  if (!session) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  return <Outlet />;
}

/** Scenario-avhengige providere (data + lokal lagring) for portalen. */
function ScenarioProviders() {
  const [sp] = useSearchParams();
  const scenarioId = getScenario(sp.get('scenario')).id;
  return (
    <DataProvider scenarioId={scenarioId}>
      <DemoStoreProvider scenarioId={scenarioId}>
        <RecordViewerProvider>
          <DemoProfileProvider>
            <AppShell />
          </DemoProfileProvider>
        </RecordViewerProvider>
      </DemoStoreProvider>
    </DataProvider>
  );
}

function NotFound() {
  return (
    <EmptyState
      type="no_results"
      title="Siden finnes ikke"
      description="Ruten er ukjent. Bruk menyen eller gå til Oversikt."
      action={
        <Button asChild size="sm">
          <Link to="/oversikt">Til Oversikt</Link>
        </Button>
      }
    />
  );
}

export default function App() {
  return (
    <SessionProvider>
      <CatalogProvider>
        <CartProvider>
          <TooltipProvider delayDuration={200}>
            <BrowserRouter>
              <Routes>
                {/* Offentlig nettside */}
                <Route element={<SiteLayout />}>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/produkter" element={<ProductsPage />} />
                  <Route path="/produkter/:slug" element={<ProductDetailPage />} />
                  <Route path="/bestill" element={<CheckoutPage />} />
                  <Route path="/bestill/bekreftet/:id" element={<OrderConfirmationPage />} />
                  <Route path="/prosjekt" element={<ProjectRequestPage />} />
                  <Route path="/prosjekt/bekreftet/:id" element={<RequestConfirmationPage />} />
                </Route>

                <Route path="/login" element={<LoginPage />} />

                {/* Produksjonsportal */}
                <Route element={<Protected />}>
                  <Route element={<ScenarioProviders />}>
                    <Route path="/oversikt" element={<OversiktPage />} />
                    <Route path="/produksjon" element={<ProduksjonPage />} />
                    <Route path="/maskiner" element={<MaskinerPage />} />
                    <Route path="/maskiner/:id" element={<MaskinDetaljPage />} />
                    <Route path="/sammenlign" element={<SammenlignPage />} />
                    <Route path="/avvik" element={<AvvikPage />} />
                    <Route path="/avvik/sak/:id" element={<AvvikSakPage />} />
                    <Route path="/avvik/:id" element={<AvvikDetaljPage />} />
                    <Route path="/rapporter" element={<RapporterPage />} />
                    <Route path="/rapporter/produksjon" element={<ProduksjonsrapportPage />} />
                    <Route path="/rapporter/:type" element={<DriftRapportPage />} />
                    <Route path="/datakilder" element={<DatakilderPage />} />
                    <Route path="/assistent" element={<AssistentPage />} />
                    <Route path="/min-dag" element={<MinDagPage />} />
                    <Route path="/prosjekter" element={<ProsjekterPage />} />
                    <Route path="/prosjekter/:id" element={<ProsjektDetaljPage />} />
                    <Route path="/timer" element={<TimerPage />} />
                    <Route path="/vareforbruk" element={<VareforbrukPage />} />
                    <Route path="/innstillinger" element={<InnstillingerPage />} />
                    <Route path="/innstillinger/:seksjon" element={<InnstillingerPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
            <Toaster position="bottom-right" closeButton />
          </TooltipProvider>
        </CartProvider>
      </CatalogProvider>
    </SessionProvider>
  );
}
